import React, { useMemo, createElement } from 'react';
import PropTypes from 'prop-types';
import AddIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DocumentList from '../../../components/lists/DocumentList';
import { getHelpVideoLink } from '../../../constants/help-tooltip-content';
import { AVAILABLE_FACTORING } from '../../../graphql/factoring/queries';
import DocumentSummary from '../../../components/summaries/DocumentSummary';
import SummaryData from '../../../components/summaries/SummaryData';
import {
  DETAILED_DATA_GRID_CLASS_NAME,
  CREDENTIALS_MIN_HEIGHT,
} from '../../../theme/constants/data-grid';
import { availableFactoringTitles } from '../../../constants/factoring/summary-titles';
import FactoringHeader from '../../../components/headers/FactoringHeader';
import AvailableFactoringFilters from '../components/filters/AvailableFactoringFilters';
import {
  CREDIT_LINE_ERROR_DIALOGS,
  CREDIT_LINE_WARNING_MESSAGES,
} from '../../../constants/credit-line-error-config';
import CreditLineErrorDialog from '../../../components/dialogs/CreditLineErrorDialog';
import { preofferSelected } from '../../../apollo/reactive-variables/invoices';
import useAvailableFactoringState from '../hooks/useAvailableFactoringState';

const STEPPER_PLACEMENT_TYPES = {
  TITLE_SIDE: 'titleSide',
  TOP: 'top',
};

const AvailableFactoringView = ({
  getColumns,
  tutorial,
  includeHeaders,
  mobileHeaders,
  summaryHeaders,
  summaryMobileHeaders,
  initialOrderBy,
  isRowSelectable,
  getRowClassName,
  initialInvoiceFilters,
  actionButtonComponent,
  warningAlertComponent,
  summaryDataInfo,
  getWarningParams,
  OverlayComponent,
  SummaryComponent,
  EvaluationSentDialogComponent,
  StepperComponent,
  stepperPlacement,
  getStepsConditions,
  headerDescription,
  useBorderedTitle,
  additionalQueries,
  getEvaluationSentDialogProps,
  enableCreditLineFlow,
  showGoBackButton,
  resolveGridMinHeight,
  summaryQueryDocument,
  TitleActionComponent,
  titleActionComponentProps,
  requireSiiCredentials,
}) => {
  const state = useAvailableFactoringState({ enableCreditLineFlow });
  const {
    selectedPreofferIds,
    isInvoicesFirstLoad,
    currency,
    company,
    newInvoices,
    readNewInvoices,
    isMobile,
    showConfirmationSummary,
    setShowConfirmationSummary,
    showEvaluationSentDialog,
    handleAddMoreInvoicesButtonClick,
    handleEvaluateButtonClick,
    stepsCallbacks,
    dialogOnClose,
    warningCode,
    creditLineErrorCode,
    debtorNames,
    closeCreditLineErrorDialog,
    handleCreditLineError,
    handleEvaluateLineClick,
    evaluateLineLoading,
    requestPreoffer,
    evaluateInvoicesLoading,
  } = state;

  const stepsConditions = useMemo(
    () => getStepsConditions({
      selectedPreofferIds,
      showConfirmationSummary,
    }),
    [getStepsConditions, selectedPreofferIds, showConfirmationSummary],
  );

  const customHeaderProps = useMemo(() => {
    const stepperProps = {
      tutorial,
      stepsConditions,
      ...(stepperPlacement === STEPPER_PLACEMENT_TYPES.TOP ? { stepsCallbacks } : {}),
    };

    const topProps = stepperPlacement === STEPPER_PLACEMENT_TYPES.TOP
      ? { TopComponent: StepperComponent, topComponentProps: stepperProps }
      : {};

    let titleSideProps = {};
    if (TitleActionComponent) {
      titleSideProps = {
        TitleSideComponent: TitleActionComponent,
        titleSideComponentProps: titleActionComponentProps,
        showRightComponentInMobile: true,
      };
    } else if (stepperPlacement === STEPPER_PLACEMENT_TYPES.TITLE_SIDE) {
      titleSideProps = {
        TitleSideComponent: StepperComponent,
        titleSideComponentProps: stepperProps,
      };
    }

    return {
      headerDescription,
      Filters: AvailableFactoringFilters,
      useBorderedTitle,
      ...topProps,
      ...titleSideProps,
    };
  }, [
    tutorial,
    stepsConditions,
    stepsCallbacks,
    stepperPlacement,
    headerDescription,
    useBorderedTitle,
    StepperComponent,
    TitleActionComponent,
    titleActionComponentProps,
  ]);

  const gridMinHeight = resolveGridMinHeight
    ? resolveGridMinHeight({ company, isInvoicesFirstLoad })
    : CREDENTIALS_MIN_HEIGHT;

  const actionButtonProps = enableCreditLineFlow
    ? {
      finalAction: handleEvaluateButtonClick,
      onCreditLineError: handleCreditLineError,
    }
    : { finalAction: handleEvaluateButtonClick };

  const evaluationSentDialogProps = getEvaluationSentDialogProps({
    open: showEvaluationSentDialog,
    onClose: dialogOnClose,
    selectedPreofferIds,
    warningCode,
    warningMessage: enableCreditLineFlow
      ? CREDIT_LINE_WARNING_MESSAGES[warningCode]
      : undefined,
  });

  return (
    <>
      <DocumentList
        initialOrderBy={initialOrderBy}
        trackerId="PREOFFERS"
        type="available-factoring"
        headerTitle="Financiamiento de facturas"
        queryDocument={AVAILABLE_FACTORING}
        additionalQueries={additionalQueries}
        skipQueryDocument={requireSiiCredentials && !company?.hasSiiCredentials}
        CustomHeader={FactoringHeader}
        customHeaderProps={customHeaderProps}
        initialInvoiceFilters={initialInvoiceFilters}
        customVariables={{
          currency,
        }}
        showFilters={false}
        emitter
        checkboxSelection
        countryFilter
        newData={newInvoices}
        readData={readNewInvoices}
        isRowSelectable={isRowSelectable}
        firstLoad={isInvoicesFirstLoad}
        includeHeaders={includeHeaders}
        mobileHeaders={mobileHeaders}
        financingStatusOptions
        onSelectionModelChange={preofferSelected}
        selectionModel={selectedPreofferIds}
        keepNonExistentRowsSelected
        preColumns={getColumns(isMobile)}
        helpVideo={getHelpVideoLink('available-factoring')}
        DataGridNoRowsOverlayComponent={OverlayComponent}
        updateCustomVariables
        dataGridClassName={DETAILED_DATA_GRID_CLASS_NAME}
        disableSelectionOnClick={false}
        mobileDensity="standard"
        showConfirmationSummary={showConfirmationSummary}
        setShowConfirmationSummary={setShowConfirmationSummary}
        rowHeight={65}
        getRowClassName={getRowClassName}
        gridMinHeight={gridMinHeight}
        useNewDataButton
        ConfirmationSummary={(
          <DocumentSummary
            summaryTitle={availableFactoringTitles.summaryTitle}
            summaryDescription={availableFactoringTitles.summaryDescription}
            documentTypeId="available-factoring"
            SummaryTable={(
              <SummaryComponent
                selectedDocuments={selectedPreofferIds}
                queryDocument={summaryQueryDocument || AVAILABLE_FACTORING}
                additionalQueries={additionalQueries}
                summaryHeaders={summaryHeaders}
                mobileHeaders={summaryMobileHeaders}
                defaultOrderBy="-folio"
                onSelectionModelChange={preofferSelected}
              />
            )}
            SummaryData={(
              <SummaryData
                title={availableFactoringTitles.summaryDataTitle}
                disclaimer={availableFactoringTitles.summaryDataDisclaimer}
                cartType="available-factoring"
                infoTitle={availableFactoringTitles.summaryDataInfoTitle}
                info={summaryDataInfo}
                WarningAlert={warningAlertComponent}
                getWarningParams={getWarningParams}
              />
            )}
            showTableButton
            tableButtonText={isMobile
              ? availableFactoringTitles.summaryTableButtonTextMobile
              : availableFactoringTitles.summaryTableButtonText}
            ActionButton={createElement(actionButtonComponent, actionButtonProps)}
            tableButtonOnClick={handleAddMoreInvoicesButtonClick}
            tableButtonIcon={isMobile ? <EditOutlinedIcon /> : <AddIcon />}
            showGoBackButton={showGoBackButton}
            onGoBackClick={handleAddMoreInvoicesButtonClick}
          />
        )}
      />
      <EvaluationSentDialogComponent {...evaluationSentDialogProps} />
      {enableCreditLineFlow && (
        <CreditLineErrorDialog
          open={!!creditLineErrorCode}
          onClose={closeCreditLineErrorDialog}
          config={CREDIT_LINE_ERROR_DIALOGS[creditLineErrorCode]}
          debtorNames={debtorNames}
          onEvaluateLine={handleEvaluateLineClick}
          evaluateLineLoading={evaluateLineLoading}
          onEvaluateInvoices={requestPreoffer}
          evaluateInvoicesLoading={evaluateInvoicesLoading}
        />
      )}
    </>
  );
};

AvailableFactoringView.propTypes = {
  getColumns: PropTypes.func.isRequired,
  tutorial: PropTypes.shape({}),
  includeHeaders: PropTypes.arrayOf(PropTypes.string),
  mobileHeaders: PropTypes.arrayOf(PropTypes.string),
  summaryHeaders: PropTypes.arrayOf(PropTypes.string),
  summaryMobileHeaders: PropTypes.arrayOf(PropTypes.string),
  initialOrderBy: PropTypes.string,
  isRowSelectable: PropTypes.func,
  getRowClassName: PropTypes.func,
  initialInvoiceFilters: PropTypes.shape({}),
  actionButtonComponent: PropTypes.elementType.isRequired,
  warningAlertComponent: PropTypes.elementType,
  summaryDataInfo: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    equation: PropTypes.elementType,
    alt: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
  })),
  getWarningParams: PropTypes.func,
  OverlayComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
  SummaryComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
  EvaluationSentDialogComponent: PropTypes.oneOfType([
    PropTypes.element, PropTypes.func]).isRequired,
  StepperComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  stepperPlacement: PropTypes.oneOf(Object.values(STEPPER_PLACEMENT_TYPES)),
  getStepsConditions: PropTypes.func,
  headerDescription: PropTypes.string,
  useBorderedTitle: PropTypes.bool,
  additionalQueries: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({})),
    PropTypes.shape({}),
  ]),
  getEvaluationSentDialogProps: PropTypes.func.isRequired,
  enableCreditLineFlow: PropTypes.bool,
  showGoBackButton: PropTypes.bool,
  resolveGridMinHeight: PropTypes.func,
  summaryQueryDocument: PropTypes.shape({}),
  TitleActionComponent: PropTypes.elementType,
  titleActionComponentProps: PropTypes.shape({}),
  requireSiiCredentials: PropTypes.bool,
};

AvailableFactoringView.defaultProps = {
  tutorial: null,
  includeHeaders: null,
  mobileHeaders: null,
  summaryHeaders: null,
  summaryMobileHeaders: null,
  initialOrderBy: null,
  isRowSelectable: null,
  getRowClassName: null,
  initialInvoiceFilters: null,
  warningAlertComponent: null,
  summaryDataInfo: null,
  getWarningParams: null,
  StepperComponent: null,
  stepperPlacement: STEPPER_PLACEMENT_TYPES.TOP,
  getStepsConditions: () => [],
  headerDescription: null,
  useBorderedTitle: false,
  additionalQueries: {},
  enableCreditLineFlow: false,
  showGoBackButton: false,
  resolveGridMinHeight: null,
  summaryQueryDocument: null,
  TitleActionComponent: null,
  titleActionComponentProps: {},
  requireSiiCredentials: true,
};

export default AvailableFactoringView;
